import type { ButtonHTMLAttributes, FC, MouseEvent, ReactNode } from "react";
import type {
	ButtonProps as AriaButtonProps,
	TooltipProps as AriaTooltipProps,
} from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

/**
 * Maps Figma IconButton Hierarchy → base Button `color`.
 * Tertiary/Link use tertiary as a base and override hover/fill via
 * `hierarchyStyles` (Link must not use link-gray — that strips padding and
 * breaks the 36/40/44 hit targets).
 */
const hierarchyToColor = {
	primary: "primary",
	secondary: "secondary",
	tertiary: "tertiary",
	link: "tertiary",
	destructive: "primary-destructive",
} as const;

/** Hierarchy-specific overrides to match Figma IconButton (325:4867). */
const hierarchyStyles = {
	primary: "",
	secondary: "",
	tertiary: [
		// Default: bg-tertiary + shadow-sm; Hover: bg-brand-solid + white icon
		"bg-tertiary shadow-sm",
		"hover:bg-brand-solid! hover:text-white data-loading:bg-brand-solid!",
		"*:data-icon:text-fg-brand-primary hover:*:data-icon:text-white!",
		"disabled:bg-disabled disabled:shadow-xs disabled:*:data-icon:text-fg-disabled_subtle",
	].join(" "),
	link: [
		// No fill; hover uses bg-primary_hover (Figma Hierarchy=Link)
		"bg-transparent! shadow-none",
		"hover:bg-primary_hover! hover:shadow-sm data-loading:bg-primary_hover!",
		"*:data-icon:text-fg-brand-primary hover:*:data-icon:text-fg-brand-primary",
		"disabled:bg-transparent disabled:shadow-none disabled:*:data-icon:text-fg-disabled_subtle",
	].join(" "),
	destructive: "",
} as const;

export type IconButtonHierarchy = keyof typeof hierarchyToColor;
export type IconButtonSize = "sm" | "md" | "lg";
export type IconButtonShape = "round" | "square";

export interface IconButtonProps
	extends Omit<
		ButtonHTMLAttributes<HTMLButtonElement>,
		"color" | "children" | "size"
	> {
	/** Visual hierarchy matching Figma IconButton. @default "secondary" */
	hierarchy?: IconButtonHierarchy;
	/** Control size — sm 36×36 / md 40×40 / lg 44×44; icon stays 20×20. @default "md" */
	size?: IconButtonSize;
	/** Round (radius-full) or square (radius-md). @default "round" */
	shape?: IconButtonShape;
	/** Leading icon component or element (20×20). */
	icon: FC<{ className?: string }> | ReactNode;
	/** Optional tooltip title shown on hover. */
	tooltip?: string;
	/** Tooltip placement relative to the button. @default "top" */
	tooltipPlacement?: AriaTooltipProps["placement"];
	/** Show a pointing arrow on the tooltip. @default false */
	tooltipArrow?: boolean;
	/** Accessible name — required when icon-only. */
	"aria-label": string;
	isDisabled?: boolean;
	/** Shows a loading spinner and disables the button. */
	isLoading?: boolean;
	/**
	 * React Aria press handler. Prefer this over `onClick` — RAC Button strips
	 * DOM `onClick` from the element (it only reaches `useButton` when present
	 * on the Aria props object; TooltipTrigger nesting can still drop it).
	 */
	onPress?: AriaButtonProps["onPress"];
}

/**
 * Icon-only button aligned to Figma IconButton (325:4867).
 *
 * Hierarchies: Primary, Secondary, Tertiary (brand-solid hover),
 * Link (no fill; primary_hover), Destructive. Shapes: Round / Square.
 */
export function IconButton({
	hierarchy = "secondary",
	size = "md",
	shape = "round",
	icon,
	tooltip,
	tooltipPlacement = "top",
	tooltipArrow = false,
	isDisabled,
	isLoading,
	className,
	onClick,
	onPress,
	"aria-label": ariaLabel,
	...props
}: IconButtonProps) {
	const button = (
		<Button
			{...props}
			aria-label={ariaLabel}
			color={hierarchyToColor[hierarchy]}
			size={size}
			iconLeading={icon}
			isDisabled={isDisabled}
			isLoading={isLoading}
			onPress={(event) => {
				onPress?.(event);
				// Bridge legacy onClick callers (MapControls, etc.) onto RAC onPress.
				onClick?.(event as unknown as MouseEvent<HTMLButtonElement>);
			}}
			className={cx(
				shape === "round"
					? "rounded-full! before:rounded-full!"
					: "rounded-md! before:rounded-[7px]!",
				hierarchyStyles[hierarchy],
				className,
			)}
		/>
	);

	if (!tooltip) return button;

	return (
		<Tooltip title={tooltip} placement={tooltipPlacement} arrow={tooltipArrow}>
			{button}
		</Tooltip>
	);
}
